import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CoordinateSystemsLookupService } from '@app/core/coordinate-systems/services/coordinate-systems-lookup.service';
import { coordinatesFieldLabel } from '@app/core/configs/coordinates-column.config';
import { LabelValuePresentationComponent } from '@app/shared/components/label-value-presentation/label-value-presentation.component';
import { FormattingDataService } from '@app/core/services/formatting-data/formatting-data.service';
import { ElementWidthClassDirective } from '@app/shared/directives/element-width-class-directive';
import { IFloatingPopupInstance } from '@app/main/main-content/floating-popup-manager/interfaces/floating-popup-instance';
import { FloatingPopupService } from '@app/main/main-content/floating-popup-manager/services/floating-popup.service';
import { FloatingPopupTabsService } from '@app/main/main-content/floating-popup-manager/services/floating-popup-tabs.service';
import { NgxPermissionsModule, NgxPermissionsService } from 'ngx-permissions';
import { PermissionName } from '@app/core/permissions/enums/permission-name.enum';
import { CommentCardComponent } from '@app/shared/components/comment/comment-card/comment-card.component';
import { FpLoggerNoiseSummaryStore } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-summary/services/fp-logger-noise-summary/fp-logger-noise-summary.store';
import { FpLoggerNoiseTabs } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/enums/fp-logger-noise-tabs.enum';
import { LeakDetectionMode } from '@app/core/enums/leak-detection-mode.enum';
import { LdLoggerNoiseTypeIconWithScoreComponent } from '@app/main/leak-detection/components/ld-helper/ld-logger-noise-type-icon-with-score/ld-logger-noise-type-icon-with-score.component';
import { LdLoggerNoiseSoundSignalLoadComponent } from '@app/main/leak-detection/components/ld-helper/ld-logger-noise-sound-signal-load/ld-logger-noise-sound-signal-load.component';
import { LdLoggerNoiseEnvNoiseEditComponent } from '@app/main/leak-detection/components/ld-helper/ld-logger-noise-env-noise-edit/ld-logger-noise-env-noise-edit.component';
import { EclIndicatorComponent } from '@app/shared/components/ecl-indicator/ecl-indicator.component';
import { LdLoggerNoiseServingCellIdComponent } from '@app/main/leak-detection/components/ld-helper/ld-logger-noise-serving-cell-id/ld-logger-noise-serving-cell-id.component';
import { CommentCardNoDataComponent } from '@app/shared/components/comment/comment-card-no-data/comment-card-no-data.component';
import { DayJSDateFormat } from '@app/core/enums/date-format.enum';
import { getLoggerMeasurementPrediction } from '@app/main/leak-detection/utils/logger-measurement-prediction';

@Component({
  selector: 'gc-fp-logger-noise-summary',
  standalone: true,
  imports: [
    TranslatePipe,
    LabelValuePresentationComponent,
    ElementWidthClassDirective,
    NgxPermissionsModule,
    CommentCardComponent,
    LdLoggerNoiseTypeIconWithScoreComponent,
    LdLoggerNoiseSoundSignalLoadComponent,
    LdLoggerNoiseEnvNoiseEditComponent,
    EclIndicatorComponent,
    LdLoggerNoiseServingCellIdComponent,
    CommentCardNoDataComponent,
  ],
  templateUrl: './fp-logger-noise-summary.component.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FpLoggerNoiseSummaryComponent {
  public readonly currentTabStore = inject(FpLoggerNoiseSummaryStore);
  protected readonly translateService = inject(TranslateService);
  protected readonly formattingData = inject(FormattingDataService);
  protected readonly coordinateSystems = inject(CoordinateSystemsLookupService);
  protected readonly floatingPopupService = inject(FloatingPopupService);
  protected readonly floatingPopupTabsService = inject(FloatingPopupTabsService);
  private readonly ngxPermissionsService = inject(NgxPermissionsService);

  protected readonly PermissionName = PermissionName;
  private readonly permissions = toSignal(this.ngxPermissionsService.permissions$);
  protected readonly isDeveloper = computed(() => {
    this.permissions();
    return !!this.ngxPermissionsService.getPermission(PermissionName.LeakDetection_Read_LoggersGrouped);
  });

  public popup = input<IFloatingPopupInstance>();

  public readonly data = toSignal(this.currentTabStore.data$, {
    initialValue: null,
  });

  public readonly deployment = computed(() => this.data()?.loggerDeployment);
  public readonly measurement = computed(() => this.data()?.loggerMeasurement);

  // Header
  public readonly loggerType = computed(() => this.deployment()?.loggerType);
  public readonly leakDetectionMode = toSignal(this.currentTabStore.leakDetectionMode$, {
    initialValue: LeakDetectionMode.LeakScore,
  });
  public readonly prediction = computed(() =>
    getLoggerMeasurementPrediction(this.measurement(), this.leakDetectionMode())
  );
  public readonly leakScore = computed(() => this.prediction().value);
  public readonly leakScoreStatus = computed(() => this.prediction().status);
  // Developer sees both metrics side by side, read raw rather than mode-selected.
  public readonly rawLeakScore = computed(() => this.measurement()?.leakScore ?? undefined);
  public readonly rawLeakScoreStatus = computed(() => this.measurement()?.leakScoreStatus ?? undefined);
  public readonly rawAiPredictor = computed(() => this.measurement()?.leakAiPredictor ?? undefined);
  public readonly rawAiPredictorStatus = computed(() => this.measurement()?.leakAiPredictorStatus ?? undefined);
  // Only when this measurement actually has AI Predictor data — otherwise the second badge would just show "no data"
  public readonly showBothMetrics = computed(() => this.isDeveloper() && this.rawAiPredictor() != null);

  // Summary fields
  public readonly soundSignalId = computed(() => this.measurement()?.soundSignalId ?? null);
  public readonly soundSignalUrl = computed(() => this.measurement()?.soundSignalUrl ?? null);
  public readonly serialNo = computed(() => this.formattingData.formatSerialNo(this.deployment()?.serialNo));
  public readonly rawSerialNo = computed(() => this.deployment()?.serialNo ?? '');
  public readonly lowestNoiseDecibel = computed(() =>
    this.formattingData.formatDecibel(this.measurement()?.lowestNoiseDecibel)
  );
  public readonly spread = computed(() => this.formattingData.formatDecibel(this.measurement()?.spread));
  public readonly measStartTime = computed(() =>
    this.formattingData.formatTime(this.measurement()?.measStartTime, DayJSDateFormat.LT)
  );
  public readonly soundSignalStartAt = computed(() =>
    this.formattingData.formatTime(this.measurement()?.soundSignalStartAt)
  );
  public readonly assetId = computed(() => this.deployment()?.assetId || this.placeholder);
  public readonly location = computed(() => this.deployment()?.location ?? this.placeholder);
  public readonly latitudeLongitude = computed(() =>
    this.formattingData.formatCoordinates(this.deployment()?.coordinates)
  );
  public readonly coordinates = computed(() =>
    this.formattingData.formatCustomCoordinates(this.deployment()?.customCoordinates)
  );

  public readonly coordinatesLabel = computed(() =>
    coordinatesFieldLabel(this.coordinateSystems.getFor(this.deployment()?.customCoordinates))
  );
  public readonly servingCellId = computed(() => this.deployment()?.servingCellId);
  public readonly envNoiseValue = computed(() => this.measurement()?.envNoiseDecibel);

  public readonly commentAuthor = computed(() => this.deployment()?.comment?.author || this.placeholder);
  public readonly commentModifiedAt = computed(() => {
    const modifiedAt = this.deployment()?.comment?.modifiedAt;
    if (!modifiedAt) {
      return this.placeholder;
    }

    return this.formattingData.formatDate(modifiedAt);
  });
  public readonly comment = computed(() => this.deployment()?.comment?.content || this.placeholder);

  // Developer-only fields
  public readonly eclValue = computed(() => this.deployment()?.eclForMeasurement);
  public readonly channelAndCell = computed(() => this.measurement()?.channelAndCell ?? this.placeholder);
  public readonly roundTripTime = computed(() => this.deployment()?.roundTripTime ?? this.placeholder);
  public readonly bandUsed = computed(() => this.deployment()?.usedBandForMeasurement ?? this.placeholder);
  public readonly scPci = computed(() => this.deployment()?.scPci ?? this.placeholder);
  public readonly scPciState = computed(() => this.deployment()?.scPciState ?? this.placeholder);
  public readonly cellTime = computed(() => this.measurement()?.cellTime ?? this.placeholder);
  public readonly syncTime = computed(() => this.measurement()?.syncTime ?? this.placeholder);
  public readonly connectionTime = computed(() => this.measurement()?.connectionTime ?? this.placeholder);
  public readonly signalType = computed(() => this.measurement()?.soundSignalType ?? this.placeholder);
  public readonly samplingRate = computed(() => this.measurement()?.samplingRate ?? this.placeholder);
  public readonly ls = computed(() => this.measurement()?.ls ?? this.placeholder);
  public readonly dbOver = computed(() => this.measurement()?.dbOver ?? this.placeholder);
  public readonly fRated = computed(() => this.measurement()?.fRated ?? this.placeholder);
  public readonly fPeaks = computed(() => this.measurement()?.fPeaks ?? this.placeholder);
  public readonly dyn = computed(() => this.measurement()?.dyn ?? this.placeholder);
  public readonly periodicity = computed(() => this.measurement()?.periodicity ?? this.placeholder);
  public readonly facCorrels = computed(() => this.measurement()?.facCorrels ?? this.placeholder);
  public readonly l1 = computed(() => this.measurement()?.l1 ?? this.placeholder);
  public readonly l3 = computed(() => this.measurement()?.l3 ?? this.placeholder);
  public readonly l4 = computed(() => this.measurement()?.l4 ?? this.placeholder);
  public readonly l5 = computed(() => this.measurement()?.l5 ?? this.placeholder);
  public readonly l6 = computed(() => this.measurement()?.l6 ?? this.placeholder);
  public readonly l7 = computed(() => this.measurement()?.l7 ?? this.placeholder);
  public readonly cCount = computed(() => this.measurement()?.cCount ?? this.placeholder);
  public readonly spectrumStdDev = computed(() => this.measurement()?.spectrumStdDev ?? this.placeholder);

  private get placeholder(): string {
    return this.formattingData.NO_DATA_VALUE_PLACEHOLDER_KEY;
  }

  public changeToCommentTab() {
    const popup = this.popup();

    if (popup) {
      if (popup.isMinimized) {
        this.floatingPopupService.maximize(popup.id);
      }

      this.floatingPopupTabsService.setActive(popup.id, FpLoggerNoiseTabs.Comments);
    }
  }
}
